import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BaseProps, TeaProduct } from '@/types/common'
import { TEA_PRODUCTS, TEA_PRODUCT_NAMES } from '@/constants/products'
import { useCurrency } from '@/contexts/CurrencyContext'
import { getProductRouteId, productsApi } from '@/api'

interface TeaProductGridProps extends BaseProps {
  products?: TeaProduct[]
  onProductClick?: (product: TeaProduct) => void
  gridCols?: 2 | 3 | 4
}

interface TeaProductCardProps {
  product: TeaProduct
  name: string
  onClick?: () => void
}

const TeaProductCard = ({ product, name, onClick }: TeaProductCardProps) => {
  const { formatProductPrice } = useCurrency()
  return (
    <div 
      className="bg-white shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full cursor-pointer group"
      onClick={onClick}
    >
      <div className="flex-1 overflow-hidden h-full">
        <img
          src={product.image}
          alt={name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-3 text-center flex-shrink-0">
        <h3 className="font-medium text-gray-800 mb-2 text-base group-hover:text-blue-600 transition-colors">
          {name}
        </h3>
        <p className="text-sm font-medium text-gray-500">
          {formatProductPrice(product.price, product.priceTwd)}
        </p>
      </div>
    </div>
  )
}

const TeaProductGrid = ({ 
  lang, 
  products,
  onProductClick,
  gridCols = 2,
  className = ''
}: TeaProductGridProps) => {
  const navigate = useNavigate()
  const [catalogProducts, setCatalogProducts] = useState(TEA_PRODUCTS)

  useEffect(() => {
    if (products) {
      return
    }

    let active = true

    void productsApi.getFeaturedProducts(TEA_PRODUCTS.length)
      .then((items) => {
        if (active && items.length > 0) {
          setCatalogProducts(items)
        }
      })
      .catch(() => {})

    return () => {
      active = false
    }
  }, [products])

  // 使用明確的類名而不是動態組合
  const gridClasses = {
    2: 'grid-cols-2',
    3: 'grid-cols-3', 
    4: 'grid-cols-4'
  }
  
  const gridClass = gridClasses[gridCols] || 'grid-cols-2'

  const displayProducts = products ?? catalogProducts

  const handleProductClick = (product: TeaProduct) => {
    if (onProductClick) {
      onProductClick(product)
      return
    }

    navigate(`/shop/${getProductRouteId(product)}`)
  }

  if (!displayProducts.length) {
    return (
      <div className={`w-full h-full px-4 flex items-center justify-center ${className}`}>
        <p className="text-gray-500">沒有產品資料</p>
      </div>
    )
  }

  return (
    <div className={`w-full h-full px-4 ${className}`}>
      <div className={`grid ${gridClass} gap-4 h-full`}>
        {displayProducts.map((product, index) => (
          <TeaProductCard
            key={`${product.key}-${index}`}
            product={product}
            name={TEA_PRODUCT_NAMES[product.key][lang]}
            onClick={() => handleProductClick(product)}
          />
        ))}
      </div>
    </div>
  )
}

export default TeaProductGrid
